import * as React from 'react';
import moment from 'moment';
import { getBackgroundColor, getFirstTwoLetters } from 'utils/Functions';
import { Column } from 'components/common/table';
import { useAuth } from 'context/AuthContext';
import { useKeycloak } from '@react-keycloak/web';

export const useTableColumns = (): Column[] => {
  const { auth } = useAuth();
  const { keycloak } = useKeycloak();
  const client =
    process.env.REACT_APP_KEYCLOAK_CLIENT_ID || 'api-service-client';
  const isFinanceUser =
    keycloak.tokenParsed?.resource_access?.[client]?.roles?.includes('finance');
  const isNtsaUser =
    keycloak.tokenParsed?.resource_access?.[client]?.roles?.includes('ntsa'); // ✅ Fixed: was incorrectly checking 'finance'

  const baseColumns: Column[] = [
    {
      header: 'Vehicle Image',
      accessorKey: 'imageUrl',
      thumbnail: {
        width: 50,
        height: 50,
      },
      minWidth: '160',
    },
    {
      header: 'Date Captured',
      accessorKey: 'dateCaptured',
      filterable: true,
      filterType: 'date',
      cell: (value: string) => moment(value).format('Do MMMM YYYY h:mm a'),
      minWidth: '160',
    },
    // ✅ Hidden from isNtsaUser
    ...(!isNtsaUser
      ? ([
          {
            header: 'Date (SMS) Processed',
            accessorKey: 'dateCreated',
            filterable: true,
            filterType: 'date',
            minWidth: '60',
            cell: (value: string) =>
              moment(value).format('Do MMMM YYYY h:mm a'),
          },
        ] as Column[])
      : []),
    {
      header: 'Ticket No',
      accessorKey: 'ticketNumber',
      filterable: true,
      filterType: 'text',
      minWidth: '60',
    },
    {
      header: 'Enforcement Status',
      accessorKey: 'enforcementStatus',
      filterable: true,
      filterType: 'select' as const,
      minWidth: '160',
      filterOptions: [
        { value: 'PENDING_OFFICER_ACTION', label: 'Pending Officer Action' },
        { value: 'EXPIRED', label: 'Expired' },
        { value: 'AWAITING_PAYMENT', label: 'Awaiting Payment' },
        { value: 'ENFORCED', label: 'Enforced' },
        { value: 'FAILED', label: 'Failed' },
        { value: 'ENFORCEMENT', label: 'Enforcement' },
        { value: 'RECALLED', label: 'Recalled' },
        {
          value: 'PENDING_CASE_CHECKER_ACTION',
          label: 'Pending Case Checker Action',
        },

        {
          value: 'SMS_DISPATCH_IN_PROGRESS',
          label: 'SMS Dispatch In Progress',
        },
      ],
      cell: (value: string) => {
        const config: Record<string, { label: string; className: string }> = {
          PENDING_OFFICER_ACTION: {
            label: 'Pending Officer Action',
            className: 'bg-yellow-100 text-yellow-800',
          },
          EXPIRED: { label: 'Expired', className: 'bg-gray-100 text-gray-700' },
          AWAITING_PAYMENT: {
            label: 'Awaiting Payment',
            className: 'bg-blue-100 text-blue-800',
          },
          ENFORCED: {
            label: 'Enforced',
            className: 'bg-green-100 text-green-800',
          },
          SMS_DISPATCH_IN_PROGRESS: {
            label: 'SMS Dispatch In Progress',
            className: 'bg-purple-100 text-purple-800',
          },
          FAILED: { label: 'Failed', className: 'bg-red-100 text-red-800' },
          ENFORCEMENT: {
            label: 'Enforcement',
            className: 'bg-green-200 text-green-800',
          },
          RECALLED: {
            label: 'Recalled',
            className: 'bg-orange-100 text-orange-800',
          },
          PENDING_CASE_CHECKER_ACTION: {
            label: 'Pending Case Checker Action',
            className: 'bg-purple-100 text-purple-800',
          },
        };
        const { label, className } = config[value] ?? {
          label: 'Unknown',
          className: 'bg-gray-100 text-gray-500',
        };
        return (
          <span
            className={`px-3 py-1 rounded-3xl text-sm font-medium flex items-center justify-center ${className}`}
          >
            {label}
          </span>
        );
      },
    },
    ...(isFinanceUser
      ? ([
          {
            header: 'Amount',
            accessorKey: 'amount',
            filterable: false,
          },
          {
            header: 'Status',
            accessorKey: 'status',
            filterable: true,
            filterType: 'select' as const,
            filterOptions: [
              { value: 'PAID', label: 'Paid' },
              { value: 'UNPAID', label: 'Unpaid' },
              { value: 'WARNING', label: 'Warning' },
            ],
            cell: (value: string) => (
              <span
                className={`${value === 'PAID' || value === null ? 'text-black' : 'text-white'} px-3 py-1 rounded-3xl justify-center flex items-center ${getBackgroundColor(value)}`}
              >
                {value === null
                  ? 'No status'
                  : value === 'PAID'
                    ? 'Paid'
                    : value === 'WARNING'
                      ? 'Warning'
                      : 'Unpaid'}
              </span>
            ),
          },
        ] as Column[])
      : []),
    {
      header: 'Vehicle Registration',
      accessorKey: 'vehicleRegistrationNumber',
      editable: true,
      filterable: true,
      filterType: 'text',
    },
    {
      header: 'Location',
      accessorKey: 'location',
      filterable: true,
      filterType: 'text',
    },
    {
      header: 'Sms status',
      accessorKey: 'smsStatus',
      filterable: isNtsaUser ? false : true,
      filterType: 'select',
      filterOptions: [
        { value: 'S', label: 'Sent' },
        { value: 'P', label: 'Pending' },
        { value: 'F', label: 'Failed' },
      ],
      cell: (value: string) => {
        if (isNtsaUser && value !== 'S') return null;
        return (
          <span
            className={`${value === 'P' || value === null ? 'text-black' : 'text-white'} px-3 py-1 rounded-3xl justify-center flex items-center ${getBackgroundColor(value)}`}
          >
            {value === null
              ? 'No status'
              : value === 'S'
                ? 'Sent'
                : value === 'P'
                  ? 'Pending'
                  : 'Failed'}
          </span>
        );
      },
    },
  ];

  const cameraColumn: Column = {
    header: 'Camera',
    accessorKey: 'violationId',
    filterable: true,
    filterType: 'select',
    filterOptions: [
      { value: 'GDS', label: 'ARH' },
      { value: 'PS', label: 'Vitronics' },
    ],
    cell: (value: string) => (
      <span>{getFirstTwoLetters(value, 2) === 'PS' ? 'Vitronics' : 'ARH'}</span>
    ),
  };

  return [
    ...baseColumns,
    ...(auth.includes('ntsa') || auth.includes('officer')
      ? []
      : [cameraColumn]),
  ];
};
